import { t } from "@travily/i18n";
import { destinationsPath, marketplaceIndexPath, membersPath, programsPath } from "@travily/routes";
import type DestinationsCard from "@travily/types/models/DestinationsCard";
import type {
  DestinationsCardLivingCostTrait,
  DestinationsCardPhotoTrait,
  DestinationsCardWeatherTrait,
} from "@travily/types/models/DestinationsCard";
import type Family from "@travily/types/models/Family";
import type { FamilyIdentityTrait } from "@travily/types/models/Family";
import type ListingsCard from "@travily/types/models/ListingsCard";
import type {
  ListingsCardPhotosPreviewTrait,
  ListingsCardPricingTrait,
  ListingsCardReviewsTrait,
  ListingsCardSellerTrait,
} from "@travily/types/models/ListingsCard";
import { Fragment, type ReactNode } from "react";
import { DestinationCard } from "@/components/DestinationCard/DestinationCard";
import {
  resolveDestinationPrice,
  resolveDestinationTemperature,
} from "@/components/DestinationCard/resolve-destination";
import { ListingCardAdapter } from "@/components/ListingCard/ListingCardAdapter";
import { ProgramCard, type ProgramCardProgram } from "@/components/ProgramCard/ProgramCard";
import { FamilyCard } from "./components/FamilyCard";
import { HeroBanner } from "./components/HeroBanner";
import { HomepageSection } from "./components/HomepageSection";
import { SeeAllCard } from "./components/SeeAllCard";

type HomeDestination = DestinationsCard &
  DestinationsCardPhotoTrait &
  DestinationsCardWeatherTrait &
  DestinationsCardLivingCostTrait;
type HomeFamily = Family & FamilyIdentityTrait;
type HomeListing = ListingsCard &
  ListingsCardPhotosPreviewTrait &
  ListingsCardPricingTrait &
  ListingsCardReviewsTrait &
  ListingsCardSellerTrait;

type FavoriteToggle = (favorableType: string, favorableId: string | number) => void;
type GetFavorited = (type: string, id: string | number) => boolean;

const FAVORABLE_TYPES = {
  destination: "Destination",
  listing: "Listing",
  program: "Program",
} as const;

export interface ShowViewProps {
  destinations: HomeDestination[];
  families: HomeFamily[];
  getFavorited?: GetFavorited;
  isAuthenticated: boolean;
  joinHref: string;
  learnMoreHref: string;
  onFavoriteToggle?: FavoriteToggle;
  listings: HomeListing[];
  programs: ProgramCardProgram[];
}

function renderDestinationsSection(
  destinations: HomeDestination[],
  onFavoriteToggle?: FavoriteToggle,
  getFavorited?: GetFavorited,
) {
  return (
    <HomepageSection
      subtitle={t("components.views.homes.show.destinations_subtitle")}
      title={t("components.views.homes.show.destinations_title")}
      viewAllHref={destinationsPath()}
    >
      {destinations.map((destination) => {
        const { temperature, weatherIcon } = resolveDestinationTemperature(destination.current_avg_temp_celsius);
        const favorited = getFavorited?.(FAVORABLE_TYPES.destination, destination.id) ?? false;
        return (
          <DestinationCard
            destination={destination}
            favorited={favorited}
            key={destination.id}
            onFavoriteToggle={
              onFavoriteToggle ? () => onFavoriteToggle(FAVORABLE_TYPES.destination, destination.id) : undefined
            }
            price={resolveDestinationPrice(destination)}
            priceGrade={destination.cost_evidence_grade}
            showDescription
            temperature={temperature}
            theme="compact"
            weatherIcon={weatherIcon}
          />
        );
      })}
      <SeeAllCard
        color="green"
        href={destinationsPath()}
        icon="map"
        label={t("components.views.homes.show.see_all_destinations")}
      />
    </HomepageSection>
  );
}

function renderProgramsSection(
  programs: ProgramCardProgram[],
  onFavoriteToggle?: FavoriteToggle,
  getFavorited?: GetFavorited,
) {
  return (
    <HomepageSection
      subtitle={t("components.views.homes.show.programs_subtitle")}
      title={t("components.views.homes.show.programs_title")}
      viewAllHref={programsPath()}
    >
      {programs.map((program) => {
        const favorited = getFavorited?.(FAVORABLE_TYPES.program, program.id) ?? false;
        return (
          <ProgramCard
            favorited={favorited}
            key={program.id}
            onFavoriteToggle={
              onFavoriteToggle ? () => onFavoriteToggle(FAVORABLE_TYPES.program, program.id) : undefined
            }
            program={program}
            showAuthor
            theme="compact"
          />
        );
      })}
      <SeeAllCard
        color="pink"
        href={programsPath()}
        icon="location"
        label={t("components.views.homes.show.see_all_programs")}
      />
    </HomepageSection>
  );
}

function renderListingsSection(
  listings: HomeListing[],
  onFavoriteToggle?: FavoriteToggle,
  getFavorited?: GetFavorited,
) {
  return (
    <HomepageSection
      subtitle={t("components.views.homes.show.products_subtitle")}
      title={t("components.views.homes.show.products_title")}
      viewAllHref={marketplaceIndexPath()}
    >
      {listings.map((listing) => {
        const favorited = getFavorited?.(FAVORABLE_TYPES.listing, listing.sqid) ?? false;
        const toggle = onFavoriteToggle ? () => onFavoriteToggle(FAVORABLE_TYPES.listing, listing.sqid) : undefined;
        return (
          <ListingCardAdapter
            favorited={favorited}
            key={listing.sqid}
            listing={listing}
            onFavoriteToggle={toggle}
            showAuthor
            theme="compact"
          />
        );
      })}
      <SeeAllCard
        color="orange"
        href={marketplaceIndexPath()}
        icon="store"
        label={t("components.views.homes.show.see_all_products")}
      />
    </HomepageSection>
  );
}

function renderFamiliesSection(families: HomeFamily[]) {
  return (
    <HomepageSection
      subtitle={t("components.views.homes.show.families_subtitle")}
      title={t("components.views.homes.show.families_title")}
      viewAllHref={membersPath()}
    >
      {families.map((family) => (
        <FamilyCard family={family} key={family.handle} theme="compact" />
      ))}
      <SeeAllCard
        color="blue"
        href={membersPath()}
        icon="members"
        label={t("components.views.homes.show.see_all_members")}
      />
    </HomepageSection>
  );
}

export function ShowView({
  destinations,
  families,
  getFavorited,
  isAuthenticated,
  joinHref,
  learnMoreHref,
  listings,
  onFavoriteToggle,
  programs,
}: ShowViewProps) {
  const sections: ReactNode[] = [
    destinations.length > 0 && renderDestinationsSection(destinations, onFavoriteToggle, getFavorited),
    programs.length > 0 && renderProgramsSection(programs, onFavoriteToggle, getFavorited),
    listings.length > 0 && renderListingsSection(listings, onFavoriteToggle, getFavorited),
    families.length > 0 && renderFamiliesSection(families),
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-4 pt-4 md:gap-8 lg:gap-10">
      {!isAuthenticated && <HeroBanner joinHref={joinHref} learnMoreHref={learnMoreHref} />}
      {sections.map((section, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: section list is order-stable across renders
        <Fragment key={index}>
          {index > 0 && <div className="border-t border-neutral-100" />}
          {section}
        </Fragment>
      ))}
    </div>
  );
}
